import { z } from 'zod';

import type { TemplateName } from '../core/assetType.js';
import type { Locator, ValidationIssue } from '../core/issues.js';
import type { ActivityCheckContext, ActivityDefinition } from './types.js';

import { toPointer } from '../core/issues.js';
import { zodErrorToIssues } from '../core/zodAdapter.js';
import { acknowledgementChecklist } from './acknowledgementChecklist.js';
import { baseActivity } from './baseActivity.js';
import { dialogue } from './dialogue.js';
import { dragOrdering } from './dragOrdering.js';
import { dragPairing } from './dragPairing.js';
import { flipCards } from './flipCards.js';
import { htmlEmbed } from './htmlEmbed.js';
import { image } from './image.js';
import { infoAccordion } from './infoAccordion.js';
import { multiChoiceMultiResponse } from './multiChoiceMultiResponse.js';
import { multiChoiceSingleResponse } from './multiChoiceSingleResponse.js';
import { phraseMatching } from './phraseMatching.js';
import { pickOne } from './pickOne.js';
import { scrollCard } from './scrollCard.js';
import { startActivity } from './startActivity.js';
import { startLearningOutcomes } from './startLearningOutcomes.js';
import { swipeSelect } from './swipeSelect.js';
import { clozeReveal, tapDefine } from './tapReveal.js';
import { questionMayBeNull } from './templatePolicy.js';
import { activityIssue, asRecord } from './types.js';
import { video } from './video.js';

/**
 * Every activity type the player can render, each as a module that can be called.
 *
 * This list is the catalogue in both directions: a producer is offered these types and
 * nothing else, and a produced activity is judged by the module for the type it claims.
 * Generating the offer from the same modules that judge the answer is the point — a
 * catalogue maintained separately from its validation drifts, and the drift shows up as
 * a model reliably producing a type nothing can check.
 */
export const ACTIVITY_DEFINITIONS: readonly ActivityDefinition[] = [
  startActivity,
  startLearningOutcomes,
  baseActivity,
  dialogue,
  pickOne,
  multiChoiceSingleResponse,
  multiChoiceMultiResponse,
  swipeSelect,
  dragOrdering,
  dragPairing,
  phraseMatching,
  tapDefine,
  clozeReveal,
  infoAccordion,
  scrollCard,
  flipCards,
  acknowledgementChecklist,
  video,
  htmlEmbed,
  image,
];

const BY_TYPE = new Map(ACTIVITY_DEFINITIONS.map(definition => [definition.type, definition]));

export const ACTIVITY_TYPES: readonly string[] = ACTIVITY_DEFINITIONS.map(d => d.type);

export const getActivityDefinition = (type: unknown): ActivityDefinition | undefined =>
  typeof type === 'string' ? BY_TYPE.get(type) : undefined;

export interface ValidateActivityOptions {
  /** The activity object — `{ name?, type, setup }`. */
  activity: unknown;
  /** The node's outcome ids (its exit-route keys), when the caller knows them. */
  outcomeIds?: string[];
  /** Path from the caller's root to the activity, for issue paths. Defaults to `activity`. */
  path?: (string | number)[];
  locator?: Locator;
  /**
   * The template the activity's document belongs to. What a setup may leave out depends
   * on it — a gameshow's quiz question is null by design. Omitted, the activity is held
   * to the strictest reading, so a caller with no document to consult never lets a hole
   * through.
   */
  templateName?: TemplateName;
}

const dispatchIssue = (
  message: string,
  path: (string | number)[],
  locator: Locator,
  currentValue?: unknown,
): ValidationIssue => ({
  code: 'activity.unknownType',
  severity: 'error',
  message,
  path,
  pointer: toPointer(path),
  locator,
  currentValue,
  constraint: { kind: 'enum', allowedValues: [...ACTIVITY_TYPES] },
});

/** Whether the type's setup schema models a `question` at all. */
const hasQuestionField = (definition: ActivityDefinition): boolean =>
  'question' in ((definition.setup as { shape?: Record<string, unknown> }).shape ?? {});

/**
 * The one setup field read per template: a null `question` is a gameshow's way of saying
 * the host asks it, and every other template's way of asking nothing.
 */
const templateIssues = (
  definition: ActivityDefinition,
  setup: Record<string, unknown>,
  ctx: ActivityCheckContext,
): ValidationIssue[] => {
  if (!hasQuestionField(definition) || setup.question !== null || questionMayBeNull(ctx.templateName)) {
    return [];
  }
  const issue = activityIssue(definition.type);
  return [issue(
    'questionNull',
    `The question is null, which only a gameshow allows (its host asks the question in dialogue). Write the question the player is asked.`,
    [...ctx.path, 'setup', 'question'],
    ctx,
    null,
  )];
};

/**
 * Validates one activity on its own: shape first, then the cross-field integrity its
 * type requires. Returns issues rather than throwing, so a caller holding a model's
 * response can hand the list straight back as the reason it was rejected.
 *
 * A draft — an activity with no `type` yet — is not an error here. It is what the writer
 * authored and what production consumes, so it validates clean and callers that need a
 * produced activity check for the type themselves.
 */
export const validateActivity = ({
  activity,
  outcomeIds,
  path = ['activity'],
  locator = {},
  templateName,
}: ValidateActivityOptions): ValidationIssue[] => {
  const fields = asRecord(activity);
  const type = fields.type;

  if (type === undefined || type === null || type === '') {
    return [];
  }

  const definition = getActivityDefinition(type);
  if (!definition) {
    return [dispatchIssue(
      `Unknown activity type ${JSON.stringify(type)}. Known types: ${ACTIVITY_TYPES.join(', ')}.`,
      [...path, 'type'],
      locator,
      type,
    )];
  }

  const setupPath = [...path, 'setup'];
  const parsed = definition.setup.safeParse(fields.setup ?? {});
  if (!parsed.success) {
    return zodErrorToIssues(parsed.error).map(issue => ({
      ...issue,
      path: [...setupPath, ...issue.path],
      pointer: toPointer([...setupPath, ...issue.path]),
      locator,
    }));
  }

  const ctx: ActivityCheckContext = {
    path,
    locator,
    ...(outcomeIds ? { outcomeIds } : {}),
    ...(templateName ? { templateName } : {}),
  };
  const setup = asRecord(fields.setup);
  return [
    ...templateIssues(definition, setup, ctx),
    ...(definition.check?.(setup, ctx) ?? []),
  ];
};

/**
 * The authoring fields hydra writes onto a draft. Every setup schema accepts them, because
 * they survive production and a validator rejecting them would reject real documents — but
 * they are the *brief*, not configuration, so the schema offered to a producer leaves them
 * out. Advertising them invites a model to write the brief back as part of its answer,
 * which is exactly what a live run did.
 */
const AUTHORING_FIELDS = ['prompt', 'promptStale', 'outcomeNotes'];

export interface ActivitySetupJsonSchemaOptions {
  /**
   * The template the produced activity will live under. The schema says what the
   * template lets the setup leave out, so a model is not offered a null it would then be
   * rejected for — or denied the one a gameshow expects.
   */
  templateName?: TemplateName;
}

/**
 * The JSON Schema for one activity type's `setup`, generated from the same zod module
 * that validates it. This is what the producer prompt hands a model, so the shape it is
 * asked for and the shape it is judged against are one definition.
 */
export const activitySetupJsonSchema = (
  type: string,
  { templateName }: ActivitySetupJsonSchemaOptions = {},
): Record<string, unknown> => {
  const definition = getActivityDefinition(type);
  if (!definition) {
    throw new Error(`No activity definition for type '${type}'. Known types: ${ACTIVITY_TYPES.join(', ')}.`);
  }

  const schema = z.toJSONSchema(definition.setup, { io: 'input' }) as Record<string, unknown>;
  const properties = schema.properties as Record<string, unknown> | undefined;
  if (properties) {
    for (const field of AUTHORING_FIELDS) {
      delete properties[field];
    }
    if (properties.question && !questionMayBeNull(templateName)) {
      properties.question = { type: 'string' };
    }
  }
  return schema;
};

/**
 * Whether the producer may write an activity of this type. A type no definition knows is left to
 * the validator to reject, so only a type marked `producible: false` is refused here.
 */
export const isProducible = (type: unknown): boolean => getActivityDefinition(type)?.producible !== false;

/** Every type a producer may choose, with its one-line summary — the menu a producer chooses from. */
export const activityCatalogue = (): { type: string; summary: string }[] =>
  ACTIVITY_DEFINITIONS
    .filter(definition => isProducible(definition.type))
    .map(({ type, summary }) => ({ type, summary }));

export type { ActivityCheckContext, ActivityDefinition } from './types.js';
